import { useCallback, useState, useContext, useEffect, useRef, Suspense } from "react";
import { Button } from "primereact/button";
import * as Yup from "yup";
import { TostMsg } from "../../../App";
import { Link, useLocation } from "react-router-dom/cjs/react-router-dom.min";
import { useFormik } from "formik";
import { useParams } from "react-router-dom/cjs/react-router-dom";
import AddQuestion from "./Add _Question";
import AddnewEmployee from "./Add_new_Employee";
import { getEmployeeList } from "../EmployeeList/Service/employeeService";
import "./pms.css";
import { GetAssessmentCycleById, SaveAssessmentCycle } from "./PMSService";
import ComponentLoader from "../../../Shared/Sidebar/ComponentLoader";

const AddNewAssessment = () => {
  let userDetails = JSON.parse(localStorage.getItem("User") || "{}");
  let toster = useContext(TostMsg);
  const { GUID } = useParams();
  const { search } = useLocation();
  const query = new URLSearchParams(search);
  const step = query.get("step");
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState("assessment");
  const [selectedCycle, setSelectedCycle] = useState(null);

  const [isEdit, setIsEdit] = useState(false);
  const [cycleId, setCycleId] = useState(0);
  const today = new Date();

  const minCycleStartDate = today.toISOString().split("T")[0];
  const [criteriaWeightage, setCriteriaWeightage] = useState(0);

  const maxCycleStartDate = new Date(
    today.getFullYear(),
    today.getMonth(),
    today.getDate() + 90,
  )
    .toISOString()
    .split("T")[0];

  useEffect(() => {
    if (step) {
      setActiveTab(step);
    } else {
      setActiveTab("assessment");
    }
  }, [step]);

  useEffect(() => {
    if (GUID) {
      setIsEdit(true);
      getAssessmentCycleForEdit();
    } else {
      setIsEdit(false);
      assessmentForm.resetForm();
    }
  }, [GUID]);

  const getAssessmentCycleForEdit = async () => {
    setLoading(true);

    try {
      const response = await GetAssessmentCycleById({ Guid: GUID });

      const cycle =
        response?.Data || response?.data || response?.Data?.Items?.[0];

      if (!cycle) {
        toster.current.error("Assessment cycle details not found.");
        return;
      }

      setSelectedCycle(cycle);

      assessmentForm.setValues({
        Id: cycle?.Id || 0,

        ReviewPeriodFrom: formatDateForInput(cycle?.ReviewPeriodFrom),

        ReviewPeriodTo: formatDateForInput(cycle?.ReviewPeriodTo),

        CycleStartDate: formatDateForInput(cycle?.CycleStartDate),

        CycleEndDate: formatDateForInput(cycle?.CycleEndDate),

        SelfAssessmentDeadline: formatDateForInput(
          cycle?.SelfAssessmentDeadline,
        ),

        ManagerReviewDeadline: formatDateForInput(cycle?.ManagerReviewDeadline),

        SkipLevelReviewDeadline: formatDateForInput(
          cycle?.SkipLevelReviewDeadline,
        ),

        NormalizationDeadline: formatDateForInput(cycle?.NormalizationDeadline),

        Title: cycle?.Title || "",

        Description: cycle?.Description || "",

        TemplateId: cycle?.TemplateId || 0,

        KraSectionWeightage: cycle?.KraSectionWeightage ?? 0,

        CriteriaSectionWeightage: cycle?.CriteriaSectionWeightage ?? 0,
      });
    } catch (error) {
      toster.current.error("Unable to load assessment cycle.");
    } finally {
      setLoading(false);
    }
  };


  const formatDateForInput = (date) => {
    if (!date) return "";

    return String(date).split("T")[0];
  };

  const AssessmentValidation = Yup.object({
    Title: Yup.string()
      .trim()
      .required("Title is required.")
      .min(3, "Title must be at least 3 characters."),

    Description: Yup.string()
      .trim()
      .required("Description is required.")
      .max(1000, "Description cannot exceed 1000 characters.")
      .nullable(),

    ReviewPeriodFrom: Yup.string().required("Review Period From is required."),

    ReviewPeriodTo: Yup.string()
      .required("Review Period To is required.")
      .test(
        "review-period",
        "Review Period To must be after Review Period From.",
        function (value) {
          const { ReviewPeriodFrom } = this.parent;

          if (!ReviewPeriodFrom || !value) return true;

          return value >= ReviewPeriodFrom;
        },
      ),

    CycleStartDate: Yup.string().required("Cycle Start Date is required."),

    CycleEndDate: Yup.string()
      .required("Cycle End Date is required.")
      .test(
        "cycle-end",
        "Cycle End Date must be after Cycle Start Date.",
        function (value) {
          const { CycleStartDate } = this.parent;

          if (!CycleStartDate || !value) return true;

          return value >= CycleStartDate;
        },
      ),

    SelfAssessmentDeadline: Yup.string().required(
      "Self Assessment Deadline is required.",
    ),

    ManagerReviewDeadline: Yup.string().required(
      "Manager Review Deadline is required.",
    ),

    SkipLevelReviewDeadline: Yup.string().required(
      "Skip Level Review Deadline is required.",
    ),

    NormalizationDeadline: Yup.string().required(
      "Normalization Deadline is required.",
    ),

    // TemplateId: Yup.number()
    //   .required("Template is required.")
    //   .min(1, "Please select a template."),

    TemplateId: Yup.number().nullable().min(0),

    KraSectionWeightage: Yup.number()
      .required("KRA weightage is required.")
      .min(0)
      .max(100),

    CriteriaSectionWeightage: Yup.number()
      .required("Criteria weightage is required.")
      .min(0)
      .max(100)
      .test(
        "total-weightage",
        "KRA and Criteria weightage must total 100%.",
        function (value) {
          const { KraSectionWeightage } = this.parent;

          return Number(KraSectionWeightage || 0) + Number(value || 0) === 100;
        },
      ),
  });

  const assessmentForm = useFormik({
    initialValues: {
      Id: 0,

      ReviewPeriodFrom: "",
      ReviewPeriodTo: "",

      CycleStartDate: "",
      CycleEndDate: "",

      SelfAssessmentDeadline: "",
      ManagerReviewDeadline: "",
      SkipLevelReviewDeadline: "",
      NormalizationDeadline: "",

      Title: "",
      Description: "",

      TemplateId: 0,

      KraSectionWeightage: 0,
      CriteriaSectionWeightage: 0,
    },

    validationSchema: AssessmentValidation,

    validateOnBlur: true,
    validateOnChange: true,
    validateOnMount: false,

    onSubmit: async (values) => {
      await handleSaveAssessmentCycle(values);
    },
  });

  const handleSaveAssessmentCycle = async (values) => {
    setLoading(true);

    try {
      const body = {
        Id: Number(values.Id || 0),

        ReviewPeriodFrom: values.ReviewPeriodFrom || "",
        ReviewPeriodTo: values.ReviewPeriodTo || "",

        CycleStartDate: values.CycleStartDate || "",
        CycleEndDate: values.CycleEndDate || "",

        SelfAssessmentDeadline: values.SelfAssessmentDeadline || "",

        ManagerReviewDeadline: values.ManagerReviewDeadline || "",

        SkipLevelReviewDeadline: values.SkipLevelReviewDeadline || "",

        NormalizationDeadline: values.NormalizationDeadline || "",

        Title: values.Title?.trim() || "",

        Description: values.Description?.trim() || "",

        TemplateId: Number(values.TemplateId || 0),

        KraSectionWeightage: Number(values.KraSectionWeightage || 0),

        CriteriaSectionWeightage: Number(values.CriteriaSectionWeightage || 0),
      };

      const response = await SaveAssessmentCycle(body);

      if (response?.StatusCode === 2000) {
        toster.current.success(
          Number(values.Id) > 0
            ? "Assessment cycle updated successfully."
            : "Assessment cycle created successfully.",
        );

        setSelectedCycle(response?.Data?.Id);

        setCriteriaWeightage(Number(values.CriteriaSectionWeightage));

        setActiveTab("question");

        const generatedCycleId = response?.Data?.Id;

        // Store generated CycleId
        setCycleId(generatedCycleId);

        if (Number(values.Id) === 0) {
          assessmentForm.resetForm();
        }
      } else {
        toster.current.error(
          response?.Message ||
            response?.message ||
            "Unable to save assessment cycle.",
        );
      }
    } catch (error) {
      toster.current.error(
        error?.response?.data?.Message ||
          error?.response?.data?.message ||
          error?.message ||
          "Something went wrong while saving assessment cycle.",
      );
    } finally {
      setLoading(false);
    }
  };

  const getCycleEndMaxDate = (startDate) => {
    if (!startDate) return "";

    const date = new Date(startDate);

    date.setMonth(date.getMonth() + 3);

    // Handle cases like Oct 31 + 3 months
    // where JavaScript can overflow into the following month.
    if (date.getDate() !== new Date(startDate).getDate()) {
      date.setDate(0);
    }

    return date.toISOString().split("T")[0];
  };

  return (
    <div className="container-fluid">
      <div className="row g-3 assessment-module">
        <div className="page-hd">
          <div className="Page-head">
            <div className="leftside-head">
              <h2 className="font-w600 mb-0">Create New Assessment Cycle</h2>
            </div>
            <div className="">
              <Link to="/Assessment/Cycles">
                <button className="btn btn-outline btn-sm btn-gray">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="17"
                    height="17"
                    fill="currentColor"
                    className="bi bi-arrow-left me-2"
                    viewBox="0 0 16 16"
                  >
                    <path
                      fill-rule="evenodd"
                      d="M15 8a.5.5 0 0 0-.5-.5H2.707l3.147-3.146a.5.5 0 1 0-.708-.708l-4 4a.5.5 0 0 0 0 .708l4 4a.5.5 0 0 0 .708-.708L2.707 8.5H14.5A.5.5 0 0 0 15 8"
                    ></path>
                  </svg>
                  Dashboard
                </button>
              </Link>
            </div>
          </div>
        </div>
        <div className="col-xl-12 mt-0">
          <ul className="assessment-stepper">
            <li
              className={`step ${activeTab === "assessment" ? "active" : ""}`}
              onClick={() => setActiveTab("assessment")}
            >
              <span
                className={`circle ${
                  activeTab === "assessment" ? "filled" : ""
                }`}
              >
                1
              </span>
              <span className="step-lbl">Assessment Parameters</span>
            </li>

            <li
              className={`step ${activeTab === "question" ? "active" : ""}`}
              // onClick={() => setActiveTab("question")}
            >
              <span
                className={`circle ${activeTab === "question" ? "filled" : ""}`}
              >
                2
              </span>
              <span className="step-lbl">Question Setup</span>
            </li>

            <li
              className={`step ${
                activeTab === "employee" ? "active-last active" : ""
              }`}
              // onClick={() => setActiveTab("employee")}
            >
              <span
                className={`circle ${activeTab === "employee" ? "filled" : ""}`}
              >
                3
              </span>
              <span className="step-lbl">Add Employee</span>
            </li>
          </ul>

          <div className="tab-content addnew-assessmentform">
            {activeTab === "assessment" && (
              <div id="assessment" className="show active">
                <form
                  onSubmit={async (e) => {
                    e.preventDefault();

                    const errors = await assessmentForm.validateForm();

                    if (Object.keys(errors).length > 0) {
                      assessmentForm.setTouched(
                        Object.keys(errors).reduce((acc, key) => {
                          acc[key] = true;
                          return acc;
                        }, {}),
                      );

                      return;
                    }

                    assessmentForm.handleSubmit(e);
                  }}
                >
                  <div className="flex flex-column h-12rem">
                    <div className="og-card-assessment">
                      <div className="step-title">
                        <i className="fa fa-calendar"></i> Assessment Details
                      </div>
                      <div className="">
                        <div className="og-form-group">
                          <label for="Type" className="form-label-file">
                            Title<i className="inValidError">*</i>
                          </label>
                          <input
                            type="text"
                            name="Title"
                            className={`form-control ${
                              assessmentForm.errors.Title &&
                              assessmentForm.touched.Title
                                ? "is-invalid"
                                : ""
                            }`}
                            value={assessmentForm.values.Title}
                            onChange={assessmentForm.handleChange}
                            onBlur={assessmentForm.handleBlur}
                          />

                          {assessmentForm.touched.Title &&
                            assessmentForm.errors.Title && (
                              <div className="invalid-feedback">
                                {assessmentForm.errors.Title}
                              </div>
                            )}
                        </div>
                      </div>
                      <div className="">
                        <div className="og-form-group">
                          <label for="Type" className="form-label-file">
                            Description<i className="inValidError">*</i>
                          </label>
                          <textarea
                            name="Description"
                            className={`form-control ${
                              assessmentForm.errors.Description &&
                              assessmentForm.touched.Description
                                ? "is-invalid"
                                : ""
                            }`}
                            value={assessmentForm.values.Description}
                            onChange={assessmentForm.handleChange}
                            onBlur={assessmentForm.handleBlur}
                            rows={3}
                          />

                          {assessmentForm.touched.Description &&
                            assessmentForm.errors.Description && (
                              <div className="invalid-feedback">
                                {assessmentForm.errors.Description}
                              </div>
                            )}
                        </div>
                      </div>

                      <div className="set-bg">
                        <div className="og-form-row">
                          <div className="field-title">
                            {" "}
                            <i
                              class="fa fa-calendar-range"
                              style={{ color: "#EC4119" }}
                            ></i>{" "}
                            Review Period
                            <span> — the period being assessed</span>
                          </div>
                        </div>
                        <div className="og-form-row">
                          <div className="og-form-group">
                            <label for="Type" className="form-label-file">
                              Period From
                              <i className="inValidError">*</i>
                            </label>
                            <input
                              type="date"
                              className={
                                "form-control" +
                                (assessmentForm.errors.ReviewPeriodFrom &&
                                assessmentForm.touched.ReviewPeriodFrom
                                  ? " is-invalid"
                                  : "")
                              }
                              name="ReviewPeriodFrom"
                              // onClick={(e) =>
                              //   e.target.showPicker && e.target.showPicker()
                              // }
                              onChange={assessmentForm.handleChange}
                              value={
                                assessmentForm.values.ReviewPeriodFrom || ""
                              }
                              onBlur={assessmentForm.handleBlur}
                            />
                            <div className="invalid-feedback">
                              {assessmentForm.errors.ReviewPeriodFrom &&
                              assessmentForm.touched.ReviewPeriodFrom
                                ? assessmentForm.errors.ReviewPeriodFrom
                                : null}
                            </div>
                          </div>
                          <div className="og-form-group">
                            <label for="Type" className="form-label-file">
                              Period To
                              <i className="inValidError">*</i>
                            </label>
                            <input
                              type="date"
                              className={
                                "form-control" +
                                (assessmentForm.errors.ReviewPeriodTo &&
                                assessmentForm.touched.ReviewPeriodTo
                                  ? " is-invalid"
                                  : "")
                              }
                              name="ReviewPeriodTo"
                              // onClick={(e) =>
                              //   e.target.showPicker && e.target.showPicker()
                              // }
                              onChange={assessmentForm.handleChange}
                              onBlur={assessmentForm.handleBlur}
                              value={assessmentForm.values.ReviewPeriodTo}
                              min={assessmentForm.values.ReviewPeriodFrom}
                            />
                            <div className="invalid-feedback">
                              {assessmentForm.errors.ReviewPeriodTo &&
                              assessmentForm.touched.ReviewPeriodTo
                                ? assessmentForm.errors.ReviewPeriodTo
                                : null}
                            </div>
                          </div>
                        </div>
                      </div>

                      <div className="set-bg">
                        <div className="og-form-row">
                          <div className="field-title">
                            <i
                              class="fa fa-stopwatch"
                              style={{ color: "#7c3aed" }}
                            ></i>{" "}
                            Process Window
                            <span> — when the appraisal activity runs</span>
                          </div>
                        </div>
                        <div className="og-form-row">
                          <div className="og-form-group">
                            <label for="Type" className="form-label-file">
                              Cycle Start Date<i className="inValidError">*</i>
                            </label>
                            <input
                              type="date"
                              className={
                                "form-control" +
                                (assessmentForm.errors.CycleStartDate &&
                                assessmentForm.touched.CycleStartDate
                                  ? " is-invalid"
                                  : "")
                              }
                              name="CycleStartDate"
                              // onClick={(e) =>
                              //   e.target.showPicker && e.target.showPicker()
                              // }
                              onChange={assessmentForm.handleChange}
                              value={assessmentForm.values.CycleStartDate}
                              min={isEdit ? undefined : minCycleStartDate}
                              max={maxCycleStartDate}
                            />
                            <div className="invalid-feedback">
                              {assessmentForm.errors.CycleStartDate &&
                              assessmentForm.touched.CycleStartDate
                                ? assessmentForm.errors.CycleStartDate
                                : null}
                            </div>
                          </div>
                          <div className="og-form-group">
                            <label for="Type" className="form-label-file">
                              Cycle End Date<i className="inValidError">*</i>
                            </label>
                            <input
                              type="date"
                              className={
                                "form-control" +
                                (assessmentForm.errors.CycleEndDate &&
                                assessmentForm.touched.CycleEndDate
                                  ? " is-invalid"
                                  : "")
                              }
                              name="CycleEndDate"
                              // onClick={(e) =>
                              //   e.target.showPicker && e.target.showPicker()
                              // }
                              onChange={assessmentForm.handleChange}
                              value={assessmentForm.values.CycleEndDate}
                              min={assessmentForm.values.CycleStartDate}
                              max={getCycleEndMaxDate(
                                assessmentForm.values.CycleStartDate,
                              )}
                            />
                            <div className="invalid-feedback">
                              {assessmentForm.errors.CycleEndDate &&
                              assessmentForm.touched.CycleEndDate
                                ? assessmentForm.errors.CycleEndDate
                                : null}
                            </div>
                          </div>
                        </div>
                      </div>

                      <div className="set-bg">
                        <div className="og-form-row">
                          <div className="field-title">
                            <i
                              className="fa fa-clock"
                              style={{ color: "#d97706" }}
                            ></i>{" "}
                            Stage Deadlines
                            <span>
                              {" "}
                              — when each appraisal stage must be completed
                            </span>
                          </div>
                        </div>

                        {/* Self Assessment */}
                        <div className="og-form-row">
                          <div className="og-form-group">
                            <label className="form-label-file">
                              Self Assessment Deadline
                              <i className="inValidError">*</i>
                            </label>

                            <input
                              type="date"
                              name="SelfAssessmentDeadline"
                              className={`form-control ${
                                assessmentForm.errors.SelfAssessmentDeadline &&
                                assessmentForm.touched.SelfAssessmentDeadline
                                  ? "is-invalid"
                                  : ""
                              }`}
                              value={
                                assessmentForm.values.SelfAssessmentDeadline
                              }
                              min={assessmentForm.values.CycleStartDate}
                              max={assessmentForm.values.CycleEndDate}
                              onChange={assessmentForm.handleChange}
                              onBlur={assessmentForm.handleBlur}
                            />

                            {assessmentForm.touched.SelfAssessmentDeadline &&
                              assessmentForm.errors.SelfAssessmentDeadline && (
                                <div className="invalid-feedback">
                                  {assessmentForm.errors.SelfAssessmentDeadline}
                                </div>
                              )}
                          </div>

                          {/* Manager Review */}
                          <div className="og-form-group">
                            <label className="form-label-file">
                              Manager Review Deadline
                              <i className="inValidError">*</i>
                            </label>

                            <input
                              type="date"
                              name="ManagerReviewDeadline"
                              className={`form-control ${
                                assessmentForm.errors.ManagerReviewDeadline &&
                                assessmentForm.touched.ManagerReviewDeadline
                                  ? "is-invalid"
                                  : ""
                              }`}
                              value={
                                assessmentForm.values.ManagerReviewDeadline
                              }
                              min={assessmentForm.values.SelfAssessmentDeadline}
                              max={assessmentForm.values.CycleEndDate}
                              onChange={assessmentForm.handleChange}
                              onBlur={assessmentForm.handleBlur}
                            />

                            {assessmentForm.touched.ManagerReviewDeadline &&
                              assessmentForm.errors.ManagerReviewDeadline && (
                                <div className="invalid-feedback">
                                  {assessmentForm.errors.ManagerReviewDeadline}
                                </div>
                              )}
                          </div>
                        </div>

                        {/* Skip Level + Normalization */}
                        <div className="og-form-row">
                          <div className="og-form-group">
                            <label className="form-label-file">
                              Skip Level Review Deadline
                              <i className="inValidError">*</i>
                            </label>

                            <input
                              type="date"
                              name="SkipLevelReviewDeadline"
                              className={`form-control ${
                                assessmentForm.errors.SkipLevelReviewDeadline &&
                                assessmentForm.touched.SkipLevelReviewDeadline
                                  ? "is-invalid"
                                  : ""
                              }`}
                              value={
                                assessmentForm.values.SkipLevelReviewDeadline
                              }
                              min={assessmentForm.values.ManagerReviewDeadline}
                              max={assessmentForm.values.CycleEndDate}
                              onChange={assessmentForm.handleChange}
                              onBlur={assessmentForm.handleBlur}
                            />

                            {assessmentForm.touched.SkipLevelReviewDeadline &&
                              assessmentForm.errors.SkipLevelReviewDeadline && (
                                <div className="invalid-feedback">
                                  {
                                    assessmentForm.errors
                                      .SkipLevelReviewDeadline
                                  }
                                </div>
                              )}
                          </div>

                          <div className="og-form-group">
                            <label className="form-label-file">
                              Normalization Deadline
                              <i className="inValidError">*</i>
                            </label>

                            <input
                              type="date"
                              name="NormalizationDeadline"
                              className={`form-control ${
                                assessmentForm.errors.NormalizationDeadline &&
                                assessmentForm.touched.NormalizationDeadline
                                  ? "is-invalid"
                                  : ""
                              }`}
                              value={
                                assessmentForm.values.NormalizationDeadline
                              }
                              min={
                                assessmentForm.values.SkipLevelReviewDeadline
                              }
                              max={assessmentForm.values.CycleEndDate}
                              onChange={assessmentForm.handleChange}
                              onBlur={assessmentForm.handleBlur}
                            />

                            {assessmentForm.touched.NormalizationDeadline &&
                              assessmentForm.errors.NormalizationDeadline && (
                                <div className="invalid-feedback">
                                  {assessmentForm.errors.NormalizationDeadline}
                                </div>
                              )}
                          </div>
                        </div>
                      </div>

                      <div className="set-bg">
                        <div className="og-form-row">
                          <div className="field-title">
                            <i
                              className="fa fa-sliders"
                              style={{ color: "#2b6cb0" }}
                            ></i>{" "}
                            Section Weightage
                            <span>
                              {" "}
                              — KRA vs Criteria contribution must total 100%
                            </span>
                          </div>
                        </div>

                        <div
                          className="scores"
                          style={{
                            display: "flex",
                            gap: "12px",
                            alignItems: "flex-end",
                          }}
                        >
                          <div className="og-sec-wt-box hi">
                            <input
                              type="number"
                              name="KraSectionWeightage"
                              value={assessmentForm.values.KraSectionWeightage}
                              min="0"
                              max="100"
                              onChange={assessmentForm.handleChange}
                              onBlur={assessmentForm.handleBlur}
                            />

                            <div className="score-title">
                              <i
                                className="fa fa-bullseye"
                                style={{ color: "#EC4119" }}
                              ></i>{" "}
                              KRA / Goals Section
                            </div>
                          </div>

                          <div className="sign">+</div>

                          <div className="og-sec-wt-box hi">
                            <input
                              type="number"
                              name="CriteriaSectionWeightage"
                              value={
                                assessmentForm.values.CriteriaSectionWeightage
                              }
                              min="0"
                              max="100"
                              onChange={assessmentForm.handleChange}
                              onBlur={assessmentForm.handleBlur}
                            />

                            <div className="score-title">
                              <i
                                className="fa fa-clipboard-list"
                                style={{ color: "#7c3aed" }}
                              ></i>{" "}
                              Criteria Section
                            </div>
                          </div>

                          <div className="sign">=</div>

                          <div className="og-sec-wt-box">
                            <div
                              className={`totalVal ${
                                Number(
                                  assessmentForm.values.KraSectionWeightage,
                                ) +
                                  Number(
                                    assessmentForm.values
                                      .CriteriaSectionWeightage,
                                  ) ===
                                100
                                  ? ""
                                  : "text-danger"
                              }`}
                            >
                              {Number(
                                assessmentForm.values.KraSectionWeightage || 0,
                              ) +
                                Number(
                                  assessmentForm.values
                                    .CriteriaSectionWeightage || 0,
                                )}
                              %
                            </div>

                            <div className="score-title">
                              <i className="fa fa-check-circle"></i> Total
                            </div>
                          </div>
                        </div>

                        {assessmentForm.errors.CriteriaSectionWeightage &&
                          assessmentForm.touched.CriteriaSectionWeightage && (
                            <div className="invalid-feedback d-block">
                              {assessmentForm.errors.CriteriaSectionWeightage}
                            </div>
                          )}
                      </div>

                      <div className="last-submit-row">
                        {/* <Button
                     label="Save and Exit"
                     className="savebutton w-auto"
                     iconPos="right"
                   /> */}
                        {/* <button

                          type="submit"
                          label="Save and Continue"
                          className="btn-og btn-og-primary"
                          iconPos="right"
                          // onClick={() => stepperRef.current.nextCallback()}
                        /> */}
                        <button type="submit" className="btn-og btn-og-primary">
                          Save and Continue
                        </button>
                      </div>
                    </div>
                  </div>
                </form>
              </div>
            )}
            {activeTab === "question" && (
              <div id="question" className="show active">
                <AddQuestion
                  cycleId={cycleId}
                  openFormWeightage={criteriaWeightage}
                  submitForm={(value) => setActiveTab(value)}
                />
              </div>
            )}
            {activeTab === "employee" && (
              <div id="employee" className="show active">
                <AddnewEmployee
                  cycleId={cycleId}
                  submitForm={(value) => setActiveTab(value)}
                />
              </div>
            )}
          </div>
        </div>
        <div className="col-xl-3 d-none">
          <div className="content">
            <div>
              <h1>About Organization</h1>
              <p>
                Lorem ipsum dolor sit, amet consectetur adipisicing elit. Aut
                praesentium nemo eum exercitationem unde.
              </p>
            </div>
            <img src="/images/pms/assessment.png" />
          </div>
        </div>
      </div>
      {loading ? (
        <Suspense>
          <ComponentLoader />
        </Suspense>
      ) : (
        ""
      )}
    </div>
  );
};

export default AddNewAssessment;
